import { baseApi } from './baseApi';

export function pickDraftId(row) {
  if (!row || typeof row !== 'object') return '';
  const uuidLike = row.draft_id ?? row.draftId ?? row.uuid;
  if (uuidLike != null && String(uuidLike).trim()) return String(uuidLike).trim();
  const id = String(row.id ?? '').trim();
  if (/^[0-9a-f]{8}-[0-9a-f-]{4,}$/i.test(id)) return id;
  return '';
}

function nestedItemArrays(obj) {
  if (!obj || typeof obj !== 'object') return [];
  return [
    obj.closing_stocks,
    obj.closingStocks,
    obj.items,
    obj.products,
    obj.rows,
    obj.line_items,
    obj.closing_stock_items,
    obj.my_tool_projects,
    obj.my_tool_projects_attributes,
    obj.projects,
  ].filter((value) => Array.isArray(value));
}

export function extractClosingStockRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    return payload.flatMap((row) => {
      if (!row || typeof row !== 'object') return [];
      const nested = nestedItemArrays(row).flat();
      if (nested.length) return extractClosingStockRows(nested);
      return [row];
    });
  }
  if (typeof payload !== 'object') return [];
  const nested = nestedItemArrays(payload).flat();
  if (nested.length) return extractClosingStockRows(nested);
  if (payload.data && payload.data !== payload) return extractClosingStockRows(payload.data);
  if (pickDraftId(payload) || payload.product_id || payload.product_name || payload.item_name) {
    return [payload];
  }
  return [];
}

export function mapClosingStockViewRow(row, index) {
  const product =
    row?.product && typeof row.product === 'object' && !Array.isArray(row.product)
      ? row.product
      : {};
  const qty = closingStockQtyValue(row);
  return {
    key: String(row?.id ?? row?.product_id ?? product.id ?? index),
    productId: String(row?.product_id ?? product.id ?? ''),
    itemCode: String(
      row?.item_code ?? row?.itemCode ?? product.item_code ?? product.itemCode ?? ''
    ).trim(),
    name: String(
      row?.product_name ??
        row?.item_name ??
        row?.name ??
        product.product_name ??
        product.name ??
        product.item_name ??
        ''
    ).trim(),
    qty: qty === '' ? '—' : qty,
  };
}

export function closingStockQtyValue(row) {
  if (!row || typeof row !== 'object') return '';
  const qty =
    row.closing_stock ??
    row.closing_qty ??
    row.quantity ??
    row.qty ??
    row.stock ??
    row.available_qty;
  return qty == null || qty === '' ? '' : String(qty);
}

export function closingStockQtyForProduct(rows, productId, itemCode) {
  const id = String(productId || '').trim();
  const code = String(itemCode || '').trim();
  if (!id && !code) return '';
  const match = (rows || []).find((row) => {
    const pid = String(row?.product_id ?? row?.productId ?? row?.product?.id ?? '').trim();
    if (id && pid === id) return true;
    if (!code) return false;
    const rowCode = String(
      row?.item_code ?? row?.itemCode ?? row?.product?.item_code ?? row?.product?.itemCode ?? ''
    ).trim();
    return Boolean(rowCode) && rowCode === code;
  });
  return closingStockQtyValue(match);
}

export function filenameFromDisposition(disposition) {
  const raw = String(disposition || '');
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(raw);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1].replace(/['"]/g, '').trim());
    } catch {
      return utf[1].replace(/['"]/g, '').trim();
    }
  }
  const plain = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(raw);
  if (plain?.[1]) return plain[1].replace(/['"]/g, '').trim();
  return 'closing_stock.csv';
}

export const closingStockApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    importClosingStockCsv: builder.mutation({
      query: ({ file, location_id, date }) => {
        const fd = new FormData();
        fd.append('file', file, file.name);
        fd.append('location_id', String(location_id));
        fd.append('date', String(date));
        return {
          url: '/api/closing_stocks/import_csv',
          method: 'POST',
          body: fd,
        };
      },
      invalidatesTags: ['ClosingStocks'],
    }),
    destroyClosingStockDraft: builder.mutation({
      query: (draftId) => ({
        url: '/api/closing_stocks/destroy',
        method: 'DELETE',
        params: { draft_id: String(draftId) },
      }),
      invalidatesTags: ['ClosingStocks'],
    }),
  }),
});

export const {
  useImportClosingStockCsvMutation,
  useDestroyClosingStockDraftMutation,
} = closingStockApi;
